/**
 * Checks if a conversation matches the active identifier filters (simplrTicketNumber, widgetId, roomId, platformTicketId).
 * Used for live query filtering since LiveQuery doesn't always respect these filters.
 *
 * @param {Object} conversation - Conversation object from live query event
 * @param {Object} identifierFilters - { simplrTicketNumber, widgetId, roomId, platformTicketId }
 * @param {boolean} partialMatch - when true, all identifier fields match by anchored prefix; otherwise exact
 * @returns {boolean} True if conversation matches all active identifier filters
 */
// NOTE(vlad|SPD-15655|2026-08-05): Mirror the server-side identifier search. When the partial-match
// toggle is on, every identifier field matches by prefix (server sends `like: "value%"`); when off,
// every field matches exactly (server sends `eq`). The `%` strip mirrors the server, which strips it
// before building the LIKE clause. `_` is left literal (it is a literal in the mongo `like`, and is
// the base/variant separator in these ids).
const matches = (conversationValue, filterValue, partialMatch) => {
  const conv = String(conversationValue ?? '');
  if (partialMatch) {
    return conv.startsWith(String(filterValue ?? '').replace(/%/g, ''));
  }

  return conv === String(filterValue ?? '');
};

export const isConversationMatchingIdentifiers = (
  conversation,
  { simplrTicketNumber, widgetId, roomId, platformTicketId },
  partialMatch = false
) => {
  if (simplrTicketNumber && !matches(conversation.simplrTicketNumber, simplrTicketNumber, partialMatch)) {
    return false;
  }

  // NOTE(vlad|SPD-14353:2026-03-04): widgetId maps to simplrChannel on conversation objects
  if (widgetId && !matches(conversation.simplrChannel, widgetId, partialMatch)) {
    return false;
  }

  if (roomId && !matches(conversation.roomId, roomId, partialMatch)) {
    return false;
  }

  if (platformTicketId && !matches(conversation.platformTicketId, platformTicketId, partialMatch)) {
    return false;
  }

  return true;
};
